import './index.css';
import { App as Antd } from 'antd';
import axios from 'axios';
import i18next from 'i18next';
import ReactDOM from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

import View from './View';
import resources from './locales';
import AuthProvider from './providers/AuthProvider';
import ServerApiProvider from './providers/ServerApiProvider';
import { profileUrl } from './routes/apiRoutes';
import { toolBoxStore } from './store/store';
import { initYandexMetrics } from './utils/metrics';
import { initGoogleAnalytics } from './utils/metrics';
import { initSentry } from './utils/sentry';

declare global {
  interface Window {
    toolboxConfig: {
      deploy: 'cloud' | 'onpremise';
      oauth_urls_prefix: string;
      language_pinned?: 'ru' | 'en';
      google_analytics_id?: string;
      yandex_metrika_id?: string;
      sentry_dsn?: string;
      chatra_id?: string;
      chatra_group_id?: string;
      ru_custom_tool_request_form_link?: string;
      en_custom_tool_request_form_link?: string;
      toolbox_expert_iframe_src?: string;
      toolbox_expert_iframe_src_en: string;
      toolbox_expert_iframe_src_ru: string;
    };
  }
}

const App = async () => {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  );

  let pinnedLocale: 'ru' | 'en' | undefined;

  try {
    const toolboxConfig = window.toolboxConfig;
    pinnedLocale = toolboxConfig.language_pinned;
    toolBoxStore.setAppConfig(toolboxConfig);
  } catch (error) {
    console.log('error getting config', error);
  }

  const i18n = i18next.createInstance();
  await i18n.use(initReactI18next).init({
    // debug: true,
    resources,
    lng: pinnedLocale || 'en',
    fallbackLng: pinnedLocale || 'en',
  });

  const tokenData = localStorage.getItem('authToken');
  if (tokenData) {
    const token = JSON.parse(tokenData).token;
    try {
      const res = await axios.get(profileUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      if (res.data.locale && Object.hasOwn(resources, res.data.locale)) {
        i18n.changeLanguage(res.data.locale);
      }

      if (res.data.username && res.data.is_privileged) {
        const {
          username,
          email,
          first_name,
          last_name,
          is_privileged,
          is_admin,
        } = res.data;

        toolBoxStore.setUserData({
          username,
          email,
          firstName: first_name,
          lastName: last_name,
          isPremium: !!is_privileged,
          isAdmin: is_admin,
        });
      }
    } catch (error) {
      console.log('Could not get and handle locale set', error);
    }
  } else if (pinnedLocale === undefined) {
    try {
      const locale = navigator.language.split('-')[0];
      i18n.changeLanguage(locale);
    } catch (error) {
      console.log('navigator.language unexpected value', error);
    }
  }

  const isBeenHereLoggedIn = localStorage.getItem('beenHere');
  const showInfo = localStorage.getItem('showInfo');
  const userWantsInfo = showInfo || false;

  if (isBeenHereLoggedIn === '1' && !userWantsInfo) {
    localStorage.setItem('showInfo', JSON.stringify({ showInfo: false }));
  }

  if (
    toolBoxStore?.deployMode !== 'onpremise' &&
    process.env.NODE_ENV === 'production'
  ) {
    initSentry();
    initYandexMetrics();
    initGoogleAnalytics();
  }

  console.log('1.3.14');

  root.render(
    <Antd>
      <AuthProvider>
        <ServerApiProvider>
          <I18nextProvider i18n={i18n}>
            <BrowserRouter>
              <View />
            </BrowserRouter>
          </I18nextProvider>
        </ServerApiProvider>
      </AuthProvider>
    </Antd>,
  );
};

export default App;
